import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import {
  Building,
  Error404,
  Nodata,
  Noreply,
  Notoken,
  Unconnect
} from '@/assets/images/nodata'
import { useWallet } from '@/hooks/useWallet'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/routes'

import { Flex } from './Box'
import { Button } from './Button'
import { Exo2Text } from './Text'

export const Placeholder: React.FC<
  {
    text?: React.ReactNode
    img?: React.ReactNode
  } & React.HTMLAttributes<HTMLDivElement>
> = ({ className, img, text, ...props }) => {
  const renderText = useCallback(() => {
    if (!text) return null
    if (typeof text === 'string')
      return (
        <Exo2Text className="text-center text-xs xl:text-base">{text}</Exo2Text>
      )
    return text
  }, [text])

  return (
    <Flex
      className={cn('flex-col items-center justify-center', className)}
      {...props}
    >
      {img}
      {renderText()}
    </Flex>
  )
}

export const NoReply: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="no-reply"
      img={<img src={Noreply} className="size-40 xl:size-[200px]" />}
      text={t('nodata.reply')}
      {...props}
    />
  )
}

export const NoConnect: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()
  const { connect } = useWallet()

  return (
    <Placeholder
      role="no-connect"
      img={<img src={Unconnect} className="size-40 xl:size-[200px]" />}
      text={
        <Flex className="flex-col">
          <Exo2Text className="text-center text-xs xl:text-base">
            {t('nodata.unconnect')}
          </Exo2Text>
          <Button
            link
            size="xs"
            className="font-Exo2 block underline"
            onClick={connect}
          >
            {t('nodata.unconnect.connect')}
          </Button>
        </Flex>
      }
      {...props}
    />
  )
}

export const NoToken: React.FC<
  React.ComponentProps<typeof Placeholder> & {
    tipsText?: string
  }
> = ({ tipsText, ...props }) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="no-token"
      img={<img src={Notoken} className="size-40 xl:size-[280px]" />}
      text={
        <Flex className="flex-col items-center">
          <Exo2Text className="text-center text-xs xl:text-base">
            {tipsText ?? t('nodata.notFoundToken')}
          </Exo2Text>
          <NavLink
            to={RoutePath.CREATE}
            className="font-Exo2 text-sm text-primary underline"
          >
            {t('nodata.notoken.start')}
          </NavLink>
        </Flex>
      }
      {...props}
    />
  )
}

export const ComingSoon: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="coming-soon"
      img={<img src={Building} className="w-full xl:w-[538px]" />}
      text={
        <Flex className="mt-4 flex-col items-center">
          <Exo2Text className="text-center text-xs xl:text-xl">
            {t('nodata.comingsoon')}
          </Exo2Text>
          <Exo2Text className="text-center text-xs xl:text-base">
            {t('nodata.comingsoon.desc')}
          </Exo2Text>
        </Flex>
      }
      {...props}
    />
  )
}

export const NoData: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="no-data"
      img={<img src={Nodata} className="size-40 xl:size-[280px]" />}
      text={t('nodata.normal')}
      {...props}
    />
  )
}

export const NoFound: React.FC<React.ComponentProps<typeof Placeholder>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <Placeholder
      role="no-found"
      img={<img src={Error404} className="size-40 xl:size-[280px]" />}
      text={t('nodata.404')}
      {...props}
    />
  )
}
